import React, { useMemo, useState } from "react";
import axios from "axios";
import {
    Box,
    Typography,
    Paper,
    TextField,
    Button,
    Collapse,
    Checkbox
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { BlockDataGrid } from "../Common/BlockDataGrid";

const LEVEL_COUNT = 10;
const RCC_COUNT = 24;

const parseMaybeNumber = (v) => {
    if (v === null || v === undefined) return null;
    if (typeof v === "number") return Number.isFinite(v) ? v : null;
    const s = String(v).trim();
    if (s === "") return null;
    const n = Number(s);
    return Number.isFinite(n) ? n : null;
};

const mean = (arr) => arr.reduce((a, b) => a + b, 0) / arr.length;

const G06 = () => {
    const [metaOpen, setMetaOpen] = useState(true);
    const [rccOpen, setRccOpen] = useState(true);
    const [roundOpen, setRoundOpen] = useState(true);
    const [resultOpen, setResultOpen] = useState(false);

    const [metadata, setMetadata] = useState({
        assayName: "",
        lotNumber: ""
    });

    const [selectedRCCs, setSelectedRCCs] = useState(
        Array(RCC_COUNT).fill(true)
    );

    const toggleRCC = (index) => {
        const copy = [...selectedRCCs];
        copy[index] = !copy[index];
        setSelectedRCCs(copy);
    };

    const [gridData, setGridData] = useState(
        Array.from({ length: LEVEL_COUNT }, (_, i) => {
            const row = { id: i, level: i + 1, concentration: null };
            for (let r = 1; r <= RCC_COUNT; r++) row[`RCC-${r}`] = null;
            return row;
        })
    );

    const [rounding, setRounding] = useState({
        significantDigits: 6,
        precisionDigits: 1,
        cvSignificantDigits: 3,
        cvPrecisionDigits: 1
    });

    const [mgccRows, setMgccRows] = useState(
        Array.from({ length: 10 }, (_, i) => ({
            id: i,
            ref: i + 1,
            conc: null,
            pooled: null,
            cv: null,
            fittedConc: null,
            fittedSignal: null
        }))
    );

    const [fitFactor, setFitFactor] = useState("");
    const [shapeFactor, setShapeFactor] = useState("");
    const [abcdn, setAbcdn] = useState({ a: "", b: "", c: "", d: "", n: "" });

    const [graphLinePoints, setGraphLinePoints] = useState([]);
    const [graphRawPoints, setGraphRawPoints] = useState([]);
    const [hoveredPoint, setHoveredPoint] = useState(null);

    const columnNames = useMemo(
        () => [
            "Concentration",
            ...Array.from({ length: RCC_COUNT }, (_, i) => `RCC-${i + 1}`)
        ],
        []
    );

    const buildPooledXY = () => {
        const enabledRccs = [];
        for (let i = 0; i < RCC_COUNT; i++) {
            if (selectedRCCs[i]) enabledRccs.push(i + 1);
        }

        const pairs = [];

        for (let rowIdx = 0; rowIdx < LEVEL_COUNT; rowIdx++) {
            const row = gridData[rowIdx];

            const x = parseMaybeNumber(row.concentration);
            if (x === null) continue;

            const ys = [];
            for (const rcc of enabledRccs) {
                const y = parseMaybeNumber(row[`RCC-${rcc}`]);
                if (y !== null) ys.push(y);
            }

            if (ys.length === 0) continue;

            const pooledY = mean(ys);
            pairs.push({ x, y: pooledY });
        }

        return {
            concs: pairs.map((p) => p.x),
            signals: pairs.map((p) => p.y),
            pairs
        };
    };

    const handleGenerate = async () => {
        setResultOpen(false);
        setHoveredPoint(null);

        const { concs, signals, pairs } = buildPooledXY();

        const payload = { assay: 1, concs, signals };

        console.log("Fit payload:", payload);
        console.log("Pooled pairs used:", pairs);

        if (concs.length < 3) {
            setFitFactor("Need at least 3 valid pooled points.");
            setShapeFactor("");
            setAbcdn({ a: "", b: "", c: "", d: "", n: "" });
            setMgccRows(
                Array.from({ length: 10 }, (_, i) => ({
                    id: i,
                    ref: i + 1,
                    conc: null,
                    pooled: null,
                    cv: null,
                    fittedConc: null,
                    fittedSignal: null
                }))
            );
            setGraphLinePoints([]);
            setGraphRawPoints([]);
            setResultOpen(true);
            return;
        }

        try {
            const response = await axios.post(
                "http://18.205.239.255:8082/api/v1/rawcurve/fit",
                payload,
                { headers: { "Content-Type": "application/json" } }
            );

            console.log("Server response:", response.data);
            const data = response.data;

            setResultOpen(true);

            setFitFactor(String(data?.status ?? ""));
            setShapeFactor(data?.max_conc ?? "");

            if (data?.status !== 1 || !Array.isArray(data.params) || data.params.length < 5) {
                setAbcdn({ a: "", b: "", c: "", d: "", n: "" });
                setGraphLinePoints([]);
                setGraphRawPoints([]);
                return;
            }

            const [A, B, C, D, N] = data.params;

            setAbcdn({
                a: A ?? "",
                b: B ?? "",
                c: C ?? "",
                d: D ?? "",
                n: N ?? ""
            });

            setGraphRawPoints(pairs);

            const mgccData = pairs.map((p, i) => ({
                id: i,
                ref: i + 1,
                conc: p.x,
                pooled: p.y,
                cv: null,
                fittedConc: p.x,
                fittedSignal: null
            }));

            while (mgccData.length < 10) {
                mgccData.push({
                    id: mgccData.length,
                    ref: mgccData.length + 1,
                    conc: null,
                    pooled: null,
                    cv: null,
                    fittedConc: null,
                    fittedSignal: null
                });
            }

            setMgccRows(mgccData);

            const curvePts = Array.isArray(data?.curve_points)
                ? data.curve_points
                    .map((p) => ({
                        x: Number(p.x),
                        y: Number(p.y)
                    }))
                    .filter((p) => Number.isFinite(p.x) && Number.isFinite(p.y))
                : [];

            console.log("Curve points from backend:", curvePts);
            setGraphLinePoints(curvePts);

        } catch (err) {
            console.error(err);
            setResultOpen(true);
            setFitFactor(`Request failed: ${err?.message || err}`);
            setShapeFactor("");
            setAbcdn({ a: "", b: "", c: "", d: "", n: "" });
            setMgccRows(
                Array.from({ length: 10 }, (_, i) => ({
                    id: i,
                    ref: i + 1,
                    conc: null,
                    pooled: null,
                    cv: null,
                    fittedConc: null,
                    fittedSignal: null
                }))
            );
            setGraphLinePoints([]);
            setGraphRawPoints([]);
        }
    };

    return (
        <Box sx={{ p: 3 }}>
            <Button
                onClick={() => setMetaOpen(!metaOpen)}
                startIcon={metaOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            >
                Assay Metadata
            </Button>

            <Collapse in={metaOpen}>
                <Paper sx={{ p: 2, mb: 2 }}>
                    <Box sx={{ display: "flex", gap: 2 }}>
                        <TextField
                            label="Assay Name"
                            sx={{ width: "10%" }}
                            value={metadata.assayName}
                            onChange={(e) =>
                                setMetadata({ ...metadata, assayName: e.target.value })
                            }
                        />
                        <TextField
                            label="Lot Number"
                            sx={{ width: "10%" }}
                            value={metadata.lotNumber}
                            onChange={(e) =>
                                setMetadata({ ...metadata, lotNumber: e.target.value })
                            }
                        />
                    </Box>
                </Paper>
            </Collapse>

            <Button
                onClick={() => setRccOpen(!rccOpen)}
                startIcon={rccOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            >
                RCC Data
            </Button>

            <Collapse in={rccOpen}>
                <Paper sx={{ p: 2, mb: 2 }}>
                    <Box
                        sx={{
                            display: "grid",
                            gridTemplateColumns: `120px repeat(${RCC_COUNT}, 120px)`,
                            mb: 1,
                            gap: 1
                        }}
                    >
                        <Typography
                            sx={{ fontWeight: "bold", textAlign: "center", lineHeight: 2 }}
                        >
                            Included
                        </Typography>
                        {Array.from({ length: RCC_COUNT }, (_, i) => (
                            <Box
                                key={`header-checkbox-${i}`}
                                sx={{ display: "flex", justifyContent: "center" }}
                            >
                                <Checkbox
                                    size="small"
                                    checked={selectedRCCs[i]}
                                    onChange={() => toggleRCC(i)}
                                />
                            </Box>
                        ))}
                    </Box>

                    <BlockDataGrid
                        columnNames={columnNames}
                        data={gridData.slice(0, LEVEL_COUNT).map((row) => {
                            const rowData = { Concentration: row.concentration };
                            for (let r = 1; r <= RCC_COUNT; r++) {
                                rowData[`RCC-${r}`] = row[`RCC-${r}`];
                            }
                            return rowData;
                        })}
                        onDataChange={(rows) => {
                            const copy = [...gridData];
                            rows.slice(0, LEVEL_COUNT).forEach((row, i) => {
                                copy[i].concentration = parseMaybeNumber(row["Concentration"]);
                                for (let r = 1; r <= RCC_COUNT; r++) {
                                    copy[i][`RCC-${r}`] = parseMaybeNumber(row[`RCC-${r}`]);
                                }
                            });
                            setGridData(copy);
                        }}
                        cellRenderer={({ rowIndex, columnKey, value, onChange }) => (
                            <TextField
                                key={`${rowIndex}-${columnKey}`}
                                size="small"
                                value={value ?? ""}
                                onChange={(e) => onChange(e.target.value)}
                            />
                        )}
                    />
                </Paper>
            </Collapse>

            <Button
                onClick={() => setRoundOpen(!roundOpen)}
                startIcon={roundOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            >
                Rounding
            </Button>

            <Collapse in={roundOpen}>
                <Paper sx={{ p: 2, mb: 2 }}>
                    <Box sx={{ display: "flex", gap: 2 }}>
                        <TextField
                            label="Sig Digits"
                            type="number"
                            value={rounding.significantDigits}
                            onChange={(e) =>
                                setRounding({
                                    ...rounding,
                                    significantDigits: Number(e.target.value)
                                })
                            }
                        />
                        <TextField
                            label="Precision"
                            type="number"
                            value={rounding.precisionDigits}
                            onChange={(e) =>
                                setRounding({
                                    ...rounding,
                                    precisionDigits: Number(e.target.value)
                                })
                            }
                        />
                        <TextField
                            label="CV Sig Digits"
                            type="number"
                            value={rounding.cvSignificantDigits}
                            onChange={(e) =>
                                setRounding({
                                    ...rounding,
                                    cvSignificantDigits: Number(e.target.value)
                                })
                            }
                        />
                        <TextField
                            label="CV Precision"
                            type="number"
                            value={rounding.cvPrecisionDigits}
                            onChange={(e) =>
                                setRounding({
                                    ...rounding,
                                    cvPrecisionDigits: Number(e.target.value)
                                })
                            }
                        />
                    </Box>
                </Paper>
            </Collapse>

            <Box sx={{ display: "flex", justifyContent: "flex-start", mb: 2 }}>
                <Button variant="contained" onClick={handleGenerate}>
                    Generate MGCC
                </Button>
            </Box>

            <Collapse in={resultOpen}>
                <Paper sx={{ p: 2 }}>
                    <Typography variant="h6">MGCC Information</Typography>

                    <Box
                        sx={{
                            display: "flex",
                            gap: 2,
                            mt: 2,
                            alignItems: "flex-start"
                        }}
                    >
                        <Box sx={{ minWidth: 520, flex: "0 0 520px" }}>
                            <Box sx={{ display: "flex", gap: 2, mb: 1 }}>
                                <TextField label="Fit Factor" fullWidth value={fitFactor} />
                                <TextField label="Shape Factor" fullWidth value={shapeFactor} />
                            </Box>

                            <Box
                                sx={{
                                    display: "grid",
                                    gridTemplateColumns: "repeat(5, 1fr)",
                                    gap: 1,
                                    mb: 2
                                }}
                            >
                                <TextField label="a" value={abcdn.a} />
                                <TextField label="b" value={abcdn.b} />
                                <TextField label="c" value={abcdn.c} />
                                <TextField label="d" value={abcdn.d} />
                                <TextField label="n" value={abcdn.n} />
                            </Box>

                            <Box
                                sx={{
                                    display: "grid",
                                    gridTemplateColumns: "1fr 1fr 1fr 1fr 1fr 1fr",
                                    gap: 1
                                }}
                            >
                                <Typography>Ref</Typography>
                                <Typography>Conc</Typography>
                                <Typography>Pooled</Typography>
                                <Typography>%CV</Typography>
                                <Typography>Fit Conc</Typography>
                                <Typography>Fit Signal</Typography>

                                {mgccRows.map((r) => (
                                    <React.Fragment key={r.id}>
                                        <Typography>{r.ref}</Typography>
                                        <TextField size="small" value={r.conc ?? ""} />
                                        <TextField size="small" value={r.pooled ?? ""} />
                                        <TextField size="small" value={r.cv ?? ""} />
                                        <TextField size="small" value={r.fittedConc ?? ""} />
                                        <TextField size="small" value={r.fittedSignal ?? ""} />
                                    </React.Fragment>
                                ))}
                            </Box>
                        </Box>

                        <Box
                            sx={{
                                minWidth: 520,
                                border: "1px solid #ccc",
                                borderRadius: 1,
                                height: 420,
                                backgroundColor: "#fafafa",
                                p: 2,
                                position: "relative"
                            }}
                        >
                            <svg
                                width="100%"
                                height="100%"
                                viewBox="0 0 900 400"
                                preserveAspectRatio="none"
                            >
                                {(() => {
                                    const all = [...graphLinePoints, ...graphRawPoints];
                                    if (all.length === 0) return null;

                                    const xs = all.map((p) => p.x);
                                    const ys = all.map((p) => p.y);

                                    const minX = Math.min(...xs);
                                    const maxX = Math.max(...xs);
                                    const minY = Math.min(...ys);
                                    const maxY = Math.max(...ys);

                                    const leftPad = 80;
                                    const rightPad = 25;
                                    const topPad = 45;
                                    const bottomPad = 55;
                                    const chartWidth = 900 - leftPad - rightPad;
                                    const chartHeight = 400 - topPad - bottomPad;

                                    const sx = (x) =>
                                        leftPad +
                                        ((x - minX) / ((maxX - minX) || 1)) * chartWidth;

                                    const sy = (y) =>
                                        topPad +
                                        chartHeight -
                                        ((y - minY) / ((maxY - minY) || 1)) * chartHeight;

                                    const linePoints = graphLinePoints
                                        .map((p) => `${sx(p.x)},${sy(p.y)}`)
                                        .join(" ");

                                    const xTicks = Array.from({ length: 5 }, (_, i) => {
                                        const value = minX + ((maxX - minX) * i) / 4;
                                        return {
                                            value,
                                            x: sx(value)
                                        };
                                    });

                                    const yTicks = Array.from({ length: 5 }, (_, i) => {
                                        const value = minY + ((maxY - minY) * (4 - i)) / 4;
                                        return {
                                            value,
                                            y: sy(value)
                                        };
                                    });

                                    return (
                                        <>
                                            <text
                                                x="450"
                                                y="24"
                                                textAnchor="middle"
                                                fontSize="18"
                                                fontWeight="bold"
                                                fill="#333"
                                            >
                                                Dose Curve Response
                                            </text>

                                            <line
                                                x1={leftPad}
                                                y1={topPad + chartHeight}
                                                x2={leftPad + chartWidth}
                                                y2={topPad + chartHeight}
                                                stroke="#555"
                                            />
                                            <line
                                                x1={leftPad}
                                                y1={topPad}
                                                x2={leftPad}
                                                y2={topPad + chartHeight}
                                                stroke="#555"
                                            />

                                            {xTicks.map((tick, i) => (
                                                <g key={`xtick-${i}`}>
                                                    <line
                                                        x1={tick.x}
                                                        y1={topPad + chartHeight}
                                                        x2={tick.x}
                                                        y2={topPad + chartHeight + 6}
                                                        stroke="#555"
                                                    />
                                                    <text
                                                        x={tick.x}
                                                        y={topPad + chartHeight + 22}
                                                        textAnchor="middle"
                                                        fontSize="11"
                                                        fill="#333"
                                                    >
                                                        {Number(tick.value).toFixed(2)}
                                                    </text>
                                                </g>
                                            ))}

                                            {yTicks.map((tick, i) => (
                                                <g key={`ytick-${i}`}>
                                                    <line
                                                        x1={leftPad - 6}
                                                        y1={tick.y}
                                                        x2={leftPad}
                                                        y2={tick.y}
                                                        stroke="#555"
                                                    />
                                                    <line
                                                        x1={leftPad}
                                                        y1={tick.y}
                                                        x2={leftPad + chartWidth}
                                                        y2={tick.y}
                                                        stroke="#e0e0e0"
                                                    />
                                                    <text
                                                        x={leftPad - 10}
                                                        y={tick.y + 4}
                                                        textAnchor="end"
                                                        fontSize="11"
                                                        fill="#333"
                                                    >
                                                        {Number(tick.value).toFixed(2)}
                                                    </text>
                                                </g>
                                            ))}

                                            <text
                                                x="450"
                                                y="392"
                                                textAnchor="middle"
                                                fontSize="13"
                                                fill="#333"
                                            >
                                                Concentration
                                            </text>

                                            <text
                                                x="20"
                                                y="200"
                                                textAnchor="middle"
                                                fontSize="13"
                                                fill="#333"
                                                transform="rotate(-90 20 200)"
                                            >
                                                Signal
                                            </text>

                                            <polyline
                                                fill="none"
                                                stroke="#1976d2"
                                                strokeWidth="2"
                                                points={linePoints}
                                            />

                                            {graphLinePoints.map((p, i) => (
                                                <circle
                                                    key={`curve-${i}`}
                                                    cx={sx(p.x)}
                                                    cy={sy(p.y)}
                                                    r={3}
                                                    fill="#1976d2"
                                                    onMouseEnter={() =>
                                                        setHoveredPoint({
                                                            type: "Curve",
                                                            x: p.x,
                                                            y: p.y,
                                                            left: `${(sx(p.x) / 900) * 100}%`,
                                                            top: `${(sy(p.y) / 400) * 100}%`
                                                        })
                                                    }
                                                    onMouseLeave={() => setHoveredPoint(null)}
                                                />
                                            ))}

                                            {graphRawPoints.map((p, i) => (
                                                <circle
                                                    key={`raw-${i}`}
                                                    cx={sx(p.x)}
                                                    cy={sy(p.y)}
                                                    r={4}
                                                    fill="#d32f2f"
                                                    onMouseEnter={() =>
                                                        setHoveredPoint({
                                                            type: "Raw",
                                                            x: p.x,
                                                            y: p.y,
                                                            left: `${(sx(p.x) / 900) * 100}%`,
                                                            top: `${(sy(p.y) / 400) * 100}%`
                                                        })
                                                    }
                                                    onMouseLeave={() => setHoveredPoint(null)}
                                                />
                                            ))}
                                        </>
                                    );
                                })()}
                            </svg>

                            {hoveredPoint && (
                                <Box
                                    sx={{
                                        position: "absolute",
                                        left: hoveredPoint.left,
                                        top: hoveredPoint.top,
                                        transform: "translate(10px, -110%)",
                                        backgroundColor: "#222",
                                        color: "#fff",
                                        px: 1.25,
                                        py: 0.75,
                                        borderRadius: 1,
                                        fontSize: 12,
                                        pointerEvents: "none",
                                        boxShadow: 3,
                                        whiteSpace: "nowrap",
                                        zIndex: 2
                                    }}
                                >
                                    <div>{hoveredPoint.type}</div>
                                    <div>x: {Number(hoveredPoint.x).toFixed(4)}</div>
                                    <div>y: {Number(hoveredPoint.y).toFixed(4)}</div>
                                </Box>
                            )}
                        </Box>
                    </Box>
                </Paper>
            </Collapse>
        </Box>
    );
};

export default G06;